import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient, useAuth, usePermissions } from "../App";
import { toast } from "sonner";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { 
  Plus, 
  Search, 
  ChevronRight,
  Calendar,
  Upload,
  X,
  LayoutGrid,
  List,
  Columns,
  DollarSign,
  Clock,
  AlertCircle,
  GripVertical,
  Settings2,
  GanttChart,
  Edit2,
  Building2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Telescope
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CurrencyInput } from "../components/ui/currency-input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
} from "../components/ui/dropdown-menu";
import FinancialSummaryStrip from "../components/programs/FinancialSummaryStrip";
import { usePmTestMode, isProgramVisibleToPm } from "../hooks/usePmTestMode";

// Unified program stages - synced with Management View
const PROGRAM_STAGES = ["Queued", "Kickoff", "Production", "On Hold", "Delivered", "Closed"];
const RISK_LEVELS = ["low", "medium", "high"];

// Default column order - stored in localStorage
const DEFAULT_COLUMN_ORDER = ["Queued", "Kickoff", "Production", "On Hold", "Delivered", "Closed"];

// Which tab a stage — and so any program sitting in it — belongs to. Paused work
// is neither in flight nor finished: leaving On Hold under Active made that count
// read as work moving when some of it was parked, and a held project is easy to
// forget once it's mixed in with the live ones. The tab row, the card list, the
// VEXOS chip count and the board's columns all read this one function, so a stage
// can't appear under two tabs or fall out of all three.
const tabForStage = (stage) =>
  stage === "Delivered" || stage === "Closed" ? "archived"
    : stage === "On Hold" ? "hold"
      : "active";

// Which tab a PROGRAM belongs to. On the Horizon is a manual flag rather than a
// stage — unsigned work still sits in Queued or Production — so it is checked
// first and beats the stage. That is the point of the flag: work nobody has
// signed shouldn't pad the count that reads as live production, which is exactly
// how Loggerhead Resort came to look like a running project. Money roll-ups have
// excluded these all along (backend/services/horizon.py); this puts the project
// list on the same footing. Every consumer reads this one function, so a program
// can't appear under two tabs or fall out of all four.
const tabForProgram = (program) =>
  program?.on_horizon ? "horizon" : tabForStage(program?.stage);

// Stage colors for visual consistency - border-transparent to avoid black borders
const STAGE_COLORS = {
  "Intake": "bg-slate-100 text-slate-700 border-transparent",
  "Queued": "bg-slate-100 text-slate-700 border-transparent",
  "Kickoff": "bg-sky-100 text-sky-700 border-transparent",
  "Production": "bg-amber-100 text-amber-700 border-transparent",
  "Revisions": "bg-violet-100 text-violet-700 border-transparent",
  "QA/Final": "bg-emerald-100 text-emerald-700 border-transparent",
  "Delivered": "bg-green-100 text-green-700 border-transparent",
  "Closed": "bg-gray-100 text-gray-600 border-transparent"
};

// ---- Shared display helpers (used by grid, list, board, gantt) ----
const getStageBadgeClass = (stage) => {
  return STAGE_COLORS[stage] || STAGE_COLORS["Intake"];
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const getProgress = (program) => {
  if (program.stage === "Delivered") return 100;
  if (program.contract_value === 0) return 0;
  return Math.round((program.cash_collected / program.contract_value) * 100);
};

// Whole days from today until a date (negative = overdue). Null when no date.
const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  target.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

// ---- List view column registry ----
// Every metric the list can show. "money" columns are hidden for users
// without financial visibility. Order/visibility is user-configurable and
// persisted in localStorage.
const LIST_COLUMNS = {
  status: { label: "Status", align: "left" },
  contract: { label: "Contract", align: "right", money: true },
  collected: { label: "Collected", align: "right", money: true },
  remaining: { label: "Remaining", align: "right", money: true },
  progress: { label: "Progress", align: "center", money: true },
  deliverables: { label: "Deliverables", align: "center" },
  daysLeft: { label: "Days Left", align: "center" },
  due: { label: "Due Date", align: "left" },
  start: { label: "Start Date", align: "left" },
  pm: { label: "PM", align: "left" },
  vexos: { label: "VEXOS", align: "center" },
  lastActivity: { label: "Last Activity", align: "left" },
};
const DEFAULT_LIST_COLUMNS = ["status", "contract", "collected", "progress", "daysLeft", "due"];
const LIST_COLUMNS_STORAGE_KEY = "programs-list-columns-v1";

// A stored column layout is usable only if every key is a known column.
const sanitizeColumns = (arr) =>
  Array.isArray(arr) && arr.length && arr.every((k) => LIST_COLUMNS[k]) ? arr : null;

// Stage order used when sorting the Status column (pipeline order, not alphabetical)
const STAGE_SORT_ORDER = ["Intake", "Queued", "Kickoff", "Production", "Revisions", "QA/Final", "On Hold", "Delivered", "Closed"];

function getListSortValue(p, key) {
  switch (key) {
    case "program": return (p.program_name || "").toLowerCase();
    case "status": {
      const idx = STAGE_SORT_ORDER.indexOf(p.stage);
      return idx === -1 ? STAGE_SORT_ORDER.length : idx;
    }
    case "contract": return p.contract_value || 0;
    case "collected": return p.cash_collected || 0;
    case "remaining": return (p.contract_value || 0) - (p.cash_collected || 0);
    case "progress": return getProgress(p);
    case "deliverables": return p.milestone_progress ?? -1;
    case "daysLeft": {
      const d = daysUntil(p.end_window);
      return d === null ? Number.POSITIVE_INFINITY : d;
    }
    case "due": return p.end_window ? new Date(p.end_window).getTime() : Number.POSITIVE_INFINITY;
    case "start": return p.start_window ? new Date(p.start_window).getTime() : Number.POSITIVE_INFINITY;
    case "pm": return (p.primary_pm_name || p.program_manager_name || "￿").toLowerCase();
    case "vexos": return p.has_vexos ? 0 : 1;
    case "lastActivity": return p.last_activity_at ? new Date(p.last_activity_at).getTime() : 0;
    default: return 0;
  }
}

// Gantt View Component
function GanttView({ programs, onEditProgram, onUpdateTimeline, getStageBadgeClass, readOnly = false }) {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [dragType, setDragType] = useState(null); // 'move', 'resize-start', 'resize-end'
  const [dragStartX, setDragStartX] = useState(0);
  const [originalDates, setOriginalDates] = useState({ start: null, end: null });
  const [previewDates, setPreviewDates] = useState({ start: null, end: null });
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  
  // Calculate date range for the Gantt chart (3 months window)
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(1); // Start of current month
  const endDate = new Date(today);
  endDate.setMonth(endDate.getMonth() + 3);
  
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  
  // Generate month headers
  const months = [];
  let currentMonth = new Date(startDate);
  while (currentMonth < endDate) {
    months.push(new Date(currentMonth));
    currentMonth.setMonth(currentMonth.getMonth() + 1);
  }
  
  // Calculate position and width for a program bar
  function getBarStyle(program) {
    if (!program.start_window || !program.end_window) {
      return { left: '0%', width: '10%', opacity: 0.3 };
    }
    
    const progStart = new Date(program.start_window);
    const progEnd = new Date(program.end_window);
    
    const startOffset = Math.max(0, (progStart - startDate) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (progEnd - progStart) / (1000 * 60 * 60 * 24));
    
    const leftPercent = (startOffset / totalDays) * 100;
    const widthPercent = (duration / totalDays) * 100;
    
    return {
      left: Math.max(0, leftPercent) + '%',
      width: Math.min(100 - leftPercent, Math.max(2, widthPercent)) + '%'
    };
  }
  
  // Convert pixel position to date
  function pixelToDate(pixelX) {
    if (!containerRef.current) return new Date();
    const containerWidth = containerRef.current.offsetWidth;
    const dayOffset = (pixelX / containerWidth) * totalDays;
    const date = new Date(startDate);
    date.setDate(date.getDate() + Math.round(dayOffset));
    return date;
  }
  
  // Handle mouse down on bar
  function handleMouseDown(e, program, type) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(program);
    setDragType(type);
    setDragStartX(e.clientX);
    setOriginalDates({
      start: program.start_window ? new Date(program.start_window) : new Date(),
      end: program.end_window ? new Date(program.end_window) : new Date()
    });
    setPreviewDates({
      start: program.start_window ? new Date(program.start_window) : new Date(),
      end: program.end_window ? new Date(program.end_window) : new Date()
    });
    setTooltipPos({ x: e.clientX, y: e.clientY });
  }
  
  // Handle mouse move
  function handleMouseMove(e) {
    if (!dragging || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragStartX;
    const daysDelta = Math.round((deltaX / containerRect.width) * totalDays);
    
    // Update tooltip position
    setTooltipPos({ x: e.clientX, y: e.clientY });
    
    let newStart = new Date(originalDates.start);
    let newEnd = new Date(originalDates.end);
    
    if (dragType === 'move') {
      newStart.setDate(newStart.getDate() + daysDelta);
      newEnd.setDate(newEnd.getDate() + daysDelta);
    } else if (dragType === 'resize-start') {
      newStart.setDate(newStart.getDate() + daysDelta);
      if (newStart >= newEnd) return;
    } else if (dragType === 'resize-end') {
      newEnd.setDate(newEnd.getDate() + daysDelta);
      if (newEnd <= newStart) return;
    }
    
    // Update preview dates for tooltip
    setPreviewDates({ start: newStart, end: newEnd });
    
    // Update visual preview
    const bar = document.querySelector(`[data-gantt-id="${dragging.id}"]`);
    if (bar) {
      const startOffset = Math.max(0, (newStart - startDate) / (1000 * 60 * 60 * 24));
      const duration = Math.max(1, (newEnd - newStart) / (1000 * 60 * 60 * 24));
      bar.style.left = (startOffset / totalDays) * 100 + '%';
      bar.style.width = (duration / totalDays) * 100 + '%';
    }
  }
  
  // Handle mouse up
  function handleMouseUp() {
    if (!dragging || !containerRef.current) {
      setDragging(null);
      setPreviewDates({ start: null, end: null });
      return;
    }
    
    const bar = document.querySelector(`[data-gantt-id="${dragging.id}"]`);
    
    if (bar) {
      const leftPercent = parseFloat(bar.style.left);
      const widthPercent = parseFloat(bar.style.width);
      
      const startDays = (leftPercent / 100) * totalDays;
      const durationDays = (widthPercent / 100) * totalDays;
      
      const newStart = new Date(startDate);
      newStart.setDate(newStart.getDate() + Math.round(startDays));
      
      const newEnd = new Date(newStart);
      newEnd.setDate(newEnd.getDate() + Math.round(durationDays));
      
      onUpdateTimeline(
        dragging.id,
        newStart.toISOString().split('T')[0],
        newEnd.toISOString().split('T')[0]
      );
    }
    
    setDragging(null);
    setDragType(null);
    setPreviewDates({ start: null, end: null });
  }
  
  // Add global mouse listeners when dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, dragStartX, originalDates, dragType]);
  
  function formatShortDate(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  
  function formatFullDate(date) {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <>
      {/* Live date preview tooltip */}
      {dragging && previewDates.start && (
        <div 
          className="fixed z-[100] bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-xl pointer-events-none"
          style={{ 
            left: tooltipPos.x + 15, 
            top: tooltipPos.y - 60,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="flex flex-col gap-1">
            {dragType === 'resize-start' && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">Start:</span>
                <span className="font-semibold">{formatFullDate(previewDates.start)}</span>
              </div>
            )}
            {dragType === 'resize-end' && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs">End:</span>
                <span className="font-semibold">{formatFullDate(previewDates.end)}</span>
              </div>
            )}
            {dragType === 'move' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs">Start:</span>
                  <span className="font-semibold">{formatFullDate(previewDates.start)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs">End:</span>
                  <span className="font-semibold">{formatFullDate(previewDates.end)}</span>
                </div>
              </>
            )}
            <div className="text-[10px] text-gray-400 mt-1 border-t border-gray-700 pt-1">
              {Math.round((previewDates.end - previewDates.start) / (1000 * 60 * 60 * 24))} days
            </div>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-gray-900" />
        </div>
      )}
      
      <Card className="border border-border overflow-hidden">
        <CardContent className="p-0">
          {/* Header with months */}
          <div className="flex border-b border-border bg-secondary/30">
            <div className="w-48 md:w-64 shrink-0 p-3 border-r border-border font-medium text-sm flex items-center gap-2">
              Program
              {readOnly && (
                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">View Only</span>
              )}
            </div>
            <div className="flex-1 flex">
              {months.map((month, idx) => (
                <div 
                  key={idx} 
                  className="flex-1 p-2 text-center text-xs md:text-sm font-medium border-r border-border last:border-r-0"
                >
                  {month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
              ))}
            </div>
          </div>
          
          {/* Program rows */}
          <div className="max-h-[600px] overflow-y-auto">
            {programs.map((program) => {
              const barStyle = getBarStyle(program);
            
            return (
              <div 
                key={program.id} 
                className="flex border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors"
              >
                {/* Program info */}
                <div className="w-48 md:w-64 shrink-0 p-3 border-r border-border">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="font-medium text-sm truncate">{program.program_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{program.client_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`${STAGE_COLORS[program.stage] || STAGE_COLORS["Intake"]} rounded-full text-[10px]`}>
                          {program.stage || "Intake"}
                        </Badge>
                      </div>
                    </div>
                    <button
                      onClick={() => onEditProgram(program)}
                      className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                      data-testid={`edit-program-${program.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Timeline bar */}
                <div 
                  ref={containerRef}
                  className="flex-1 relative h-20 bg-gray-50"
                >
                  {/* Grid lines for months */}
                  <div className="absolute inset-0 flex pointer-events-none">
                    {months.map((_, idx) => (
                      <div key={idx} className="flex-1 border-r border-gray-100 last:border-r-0" />
                    ))}
                  </div>
                  
                  {/* Week markers */}
                  <div className="absolute inset-0 pointer-events-none">
                    {Array.from({ length: Math.ceil(totalDays / 7) }, (_, i) => {
                      const weekOffset = i * 7;
                      const leftPercent = (weekOffset / totalDays) * 100;
                      return (
                        <div 
                          key={i}
                          className="absolute top-0 bottom-0 w-px bg-gray-200"
                          style={{ left: `${leftPercent}%` }}
                        >
                          <span className="absolute -top-5 left-0 text-[8px] text-gray-400 -translate-x-1/2">
                            W{i + 1}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Today marker */}
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-10"
                    style={{ left: ((today - startDate) / (1000 * 60 * 60 * 24) / totalDays * 100) + '%' }}
                  >
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[8px] bg-red-100 text-red-600 px-1 rounded">Today</span>
                  </div>
                  
                  {/* Program bar */}
                  <div
                    data-gantt-id={program.id}
                    className={`absolute top-3 h-14 rounded-lg shadow-sm ${readOnly ? 'cursor-default' : 'cursor-move'} group ${
                      STAGE_COLORS[program.stage] || STAGE_COLORS["Intake"]
                    } border-2`}
                    style={barStyle}
                    onMouseDown={readOnly ? undefined : (e) => handleMouseDown(e, program, 'move')}
                    data-testid={`gantt-bar-${program.id}`}
                  >
                    {/* Left resize handle - only shown when not readOnly */}
                    {!readOnly && (
                      <div
                        className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize bg-gray-400/0 hover:bg-gray-400/30 rounded-l-lg"
                        onMouseDown={(e) => handleMouseDown(e, program, 'resize-start')}
                      />
                    )}
                    
                    {/* Bar content */}
                    <div className="px-2 py-1 h-full flex flex-col justify-center overflow-hidden">
                      <p className="text-xs font-medium truncate">{program.program_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatShortDate(program.start_window)} - {formatShortDate(program.end_window)}
                      </p>
                    </div>
                    
                    {/* Right resize handle - only shown when not readOnly */}
                    {!readOnly && (
                      <div
                        className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-gray-400/0 hover:bg-gray-400/30 rounded-r-lg"
                        onMouseDown={(e) => handleMouseDown(e, program, 'resize-end')}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {programs.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No programs to display
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
}

// ---- List view with configurable columns ----
// Headers are click-to-sort, drag-to-reorder, and the trailing "+" menu
// toggles which metric columns are shown. Column layout is controlled by the
// parent, which persists it to the user's account.
function ProgramsListTable({ programs, showMoney, onToggleVexos, columns, onColumnsChange }) {
  const [sort, setSort] = useState(null); // { key, dir: "asc" | "desc" } | null
  const [dragKey, setDragKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const visibleColumns = columns.filter((k) => LIST_COLUMNS[k] && (showMoney || !LIST_COLUMNS[k].money));
  const availableColumns = Object.keys(LIST_COLUMNS).filter((k) => showMoney || !LIST_COLUMNS[k].money);

  const persistColumns = (next) => {
    onColumnsChange(next);
  };

  const toggleColumn = (key) => {
    if (columns.includes(key)) {
      persistColumns(columns.filter((k) => k !== key));
    } else {
      persistColumns([...columns, key]);
    }
  };

  const resetColumns = () => {
    persistColumns(DEFAULT_LIST_COLUMNS);
    setSort(null);
    toast.success("Columns reset to default");
  };

  // Click a header: asc → desc → off
  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const handleDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) {
      setDragKey(null);
      setDragOverKey(null);
      return;
    }
    const next = columns.filter((k) => k !== dragKey);
    const targetIdx = next.indexOf(targetKey);
    next.splice(targetIdx === -1 ? next.length : targetIdx, 0, dragKey);
    persistColumns(next);
    setDragKey(null);
    setDragOverKey(null);
  };

  const sortedPrograms = useMemo(() => {
    if (!sort) return programs;
    return [...programs].sort((a, b) => {
      const va = getListSortValue(a, sort.key);
      const vb = getListSortValue(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [programs, sort]);

  // Money totals count contracted work only: an On the Horizon project is listed
  // with its own figures but isn't signed, so it never joins the footer sums.
  // Same rule as the backend roll-ups — see backend/services/horizon.py.
  const contractedPrograms = useMemo(
    () => sortedPrograms.filter((p) => !p.on_horizon),
    [sortedPrograms]
  );
  const horizonCount = sortedPrograms.length - contractedPrograms.length;

  const alignClass = (align) =>
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

  const renderCell = (program, key) => {
    switch (key) {
      case "status":
        return (
          <Badge className={`${getStageBadgeClass(program.stage)} rounded-full text-xs`}>
            {program.stage || "Intake"}
          </Badge>
        );
      case "contract":
        return <span className="font-medium text-sm">{formatCurrency(program.contract_value)}</span>;
      case "collected":
        return <span className="text-green-600 font-medium text-sm">{formatCurrency(program.cash_collected)}</span>;
      case "remaining": {
        const remaining = (program.contract_value || 0) - (program.cash_collected || 0);
        return (
          <span className={`font-medium text-sm ${remaining > 0 ? "text-amber-600" : "text-muted-foreground"}`}>
            {formatCurrency(remaining)}
          </span>
        );
      }
      case "progress":
        return (
          <div className="flex items-center gap-3 justify-center">
            <div className="w-24">
              <Progress value={getProgress(program)} className="h-1.5" />
            </div>
            <span className="text-xs text-muted-foreground w-10">{getProgress(program)}%</span>
          </div>
        );
      case "deliverables":
        return program.milestone_total > 0 ? (
          <div className="flex items-center gap-2 justify-center">
            <span className="text-xs font-medium">
              {program.milestone_completed}/{program.milestone_total}
            </span>
            <div className="w-16">
              <Progress value={program.milestone_progress || 0} className="h-1.5" />
            </div>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      case "daysLeft": {
        const d = daysUntil(program.end_window);
        if (d === null) return <span className="text-xs text-muted-foreground">—</span>;
        if (program.stage === "Delivered" || program.stage === "Closed") {
          return <span className="text-xs text-muted-foreground">Done</span>;
        }
        if (d < 0) {
          return (
            <span className="text-xs font-semibold text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full">
              {Math.abs(d)}d overdue
            </span>
          );
        }
        if (d <= 7) {
          return (
            <span className="text-xs font-semibold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">
              {d}d
            </span>
          );
        }
        return <span className="text-xs text-muted-foreground">{d}d</span>;
      }
      case "due":
        return program.end_window ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(program.end_window).toLocaleDateString()}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      case "start":
        return program.start_window ? (
          <span className="text-xs text-muted-foreground">
            {new Date(program.start_window).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      case "pm": {
        const pmName = program.primary_pm_name || program.program_manager_name;
        return pmName ? (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-medium shrink-0">
              {pmName.charAt(0)}
            </div>
            <span className="text-xs font-medium truncate max-w-[120px]">{pmName}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      }
      case "vexos":
        return (
          <button
            onClick={() => onToggleVexos?.(program)}
            title={program.has_vexos ? "Click to remove VEXOS tag" : "Click to tag as VEXOS"}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${
              program.has_vexos
                ? "bg-indigo-600 text-white hover:bg-indigo-500"
                : "text-muted-foreground border border-dashed border-border hover:text-foreground hover:border-foreground/40"
            }`}
            data-testid={`vexos-toggle-${program.id}`}
          >
            <Building2 className="w-3 h-3" />
            VEXOS
          </button>
        );
      case "lastActivity":
        return program.last_activity_at ? (
          <span className="text-xs text-muted-foreground">
            {new Date(program.last_activity_at).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        );
      default:
        return null;
    }
  };

  const renderTotalCell = (key) => {
    switch (key) {
      case "contract":
        return (
          <>
            <span className="font-semibold text-sm">
              {formatCurrency(contractedPrograms.reduce((sum, p) => sum + (p.contract_value || 0), 0))}
            </span>
            <p className="text-[10px] text-muted-foreground">Total Contracted</p>
          </>
        );
      case "collected":
        return (
          <>
            <span className="text-green-600 font-semibold text-sm">
              {formatCurrency(contractedPrograms.reduce((sum, p) => sum + (p.cash_collected || 0), 0))}
            </span>
            <p className="text-[10px] text-muted-foreground">Total Collected</p>
          </>
        );
      case "remaining":
        return (
          <>
            <span className="text-amber-600 font-semibold text-sm">
              {formatCurrency(contractedPrograms.reduce(
                (sum, p) => sum + ((p.contract_value || 0) - (p.cash_collected || 0)), 0
              ))}
            </span>
            <p className="text-[10px] text-muted-foreground">Total Remaining</p>
          </>
        );
      case "progress": {
        const totalContract = contractedPrograms.reduce((sum, p) => sum + (p.contract_value || 0), 0);
        const totalCollected = contractedPrograms.reduce((sum, p) => sum + (p.cash_collected || 0), 0);
        const pct = totalContract > 0 ? Math.round((totalCollected / totalContract) * 100) : 0;
        return <span className="font-semibold text-sm text-muted-foreground">{pct}%</span>;
      }
      case "deliverables": {
        const done = sortedPrograms.reduce((sum, p) => sum + (p.milestone_completed || 0), 0);
        const total = sortedPrograms.reduce((sum, p) => sum + (p.milestone_total || 0), 0);
        return total > 0 ? (
          <span className="font-semibold text-sm text-muted-foreground">{done}/{total}</span>
        ) : null;
      }
      default:
        return null;
    }
  };

  const sortIcon = (key) => {
    if (sort?.key !== key) {
      return <ArrowUpDown className="w-3 h-3 opacity-0 group-hover/th:opacity-60 transition-opacity" />;
    }
    return sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const showTotals = showMoney && visibleColumns.some((k) => LIST_COLUMNS[k].money);

  return (
    <Card className="border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-secondary/30">
              {/* Program column — always first, sortable, not draggable */}
              <th
                className="text-left px-6 py-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none group/th"
                onClick={() => toggleSort("program")}
                data-testid="list-header-program"
              >
                <span className="inline-flex items-center gap-1.5">
                  Program
                  {sortIcon("program")}
                </span>
              </th>
              {visibleColumns.map((key) => (
                <th
                  key={key}
                  draggable
                  onDragStart={() => setDragKey(key)}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverKey !== key) setDragOverKey(key);
                  }}
                  onDragLeave={() => setDragOverKey((prev) => (prev === key ? null : prev))}
                  onDrop={() => handleDrop(key)}
                  onDragEnd={() => {
                    setDragKey(null);
                    setDragOverKey(null);
                  }}
                  onClick={() => toggleSort(key)}
                  className={`${alignClass(LIST_COLUMNS[key].align)} px-6 py-4 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none group/th transition-colors ${
                    dragOverKey === key && dragKey !== key ? "bg-primary/10" : ""
                  } ${dragKey === key ? "opacity-40" : ""}`}
                  title="Click to sort · drag to reorder"
                  data-testid={`list-header-${key}`}
                >
                  <span className={`inline-flex items-center gap-1.5 ${LIST_COLUMNS[key].align === "right" ? "flex-row-reverse" : ""}`}>
                    <GripVertical className="w-3 h-3 opacity-0 group-hover/th:opacity-40 cursor-grab" />
                    {LIST_COLUMNS[key].label}
                    {sortIcon(key)}
                  </span>
                </th>
              ))}
              {/* "+" column picker */}
              <th className="px-4 py-3 w-16 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      title="Add or remove columns"
                      data-testid="list-columns-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuLabel>Columns</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {availableColumns.map((key) => (
                      <DropdownMenuCheckboxItem
                        key={key}
                        checked={columns.includes(key)}
                        onCheckedChange={() => toggleColumn(key)}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {LIST_COLUMNS[key].label}
                      </DropdownMenuCheckboxItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={resetColumns}>Reset to default</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedPrograms.map((program) => (
              <tr key={program.id} className="hover:bg-secondary/20 transition-colors group">
                <td className="px-6 py-4">
                  <Link to={`/programs/${program.id}`} className="block">
                    <p className="font-medium text-sm group-hover:text-primary transition-colors" style={{ fontFamily: 'var(--font-sans)' }}>
                      {program.program_name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{program.client_name}</p>
                    {program.on_horizon && (
                      <span
                        className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-estate-50 text-estate-700 border border-estate-200"
                        title="On the Horizon — not signed yet, so it's left out of the totals"
                      >
                        <Telescope className="w-2.5 h-2.5" />
                        On the Horizon
                      </span>
                    )}
                  </Link>
                </td>
                {visibleColumns.map((key) => (
                  <td key={key} className={`px-6 py-4 ${alignClass(LIST_COLUMNS[key].align)}`}>
                    {renderCell(program, key)}
                  </td>
                ))}
                <td className="px-4 py-4 text-right">
                  <Link to={`/programs/${program.id}`}>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors inline-block" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          {showTotals && (
            <tfoot>
              <tr className="bg-muted/50 border-t-2 border-border font-medium">
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">
                    Totals ({contractedPrograms.length} programs)
                  </span>
                  {horizonCount > 0 && (
                    <p className="text-[10px] text-estate-700 mt-0.5">
                      Excludes {horizonCount} on the horizon
                    </p>
                  )}
                </td>
                {visibleColumns.map((key) => (
                  <td key={key} className={`px-6 py-4 ${alignClass(LIST_COLUMNS[key].align)}`}>
                    {renderTotalCell(key)}
                  </td>
                ))}
                <td className="px-4 py-4"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}

const ProgramsView = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [clients, setClients] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid, list, kanban
  const [activeTab, setActiveTab] = useState("active"); // active, horizon, hold, archived — see tabForProgram
  const [vexosFilter, setVexosFilter] = useState(false);
  // List column layout — loaded from the user's account, localStorage as fallback
  const [listColumns, setListColumns] = useState(null);
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem("programs-column-order");
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_ORDER;
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // PM Test Mode - filter programs for selected PM
  const pmTestMode = usePmTestMode();
  
  // Role-based access - use permissions from backend
  const isPM = user?.role === "PROJECT_MANAGER" || user?.role === "Program Manager";
  const { canViewFinancials: permCanViewFinancials } = usePermissions();
  const canViewFinancials = permCanViewFinancials && !pmTestMode?.isActive;
  
  // Dialogs
  const [showNewProgram, setShowNewProgram] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showNewProgramType, setShowNewProgramType] = useState(false);
  const [showPdfUpload, setShowPdfUpload] = useState(false);
  
  // Form states
  const [newProgram, setNewProgram] = useState({
    program_name: "",
    program_type_id: "",
    client_id: "",
    contract_value: 0,
    start_window: "",
    end_window: ""
  });
  const [newClient, setNewClient] = useState({ name: "", primary_contact: "" });
  const [newProgramType, setNewProgramType] = useState({
    name: "",
    default_timeline_days: 30,
    revision_policy: "2 rounds included"
  });
  
  // PDF Upload
  const [pdfFile, setPdfFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [pendingDeliverables, setPendingDeliverables] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Edit Program Dialog
  const [editingProgram, setEditingProgram] = useState(null);
  const [editFormData, setEditFormData] = useState({
    program_name: "",
    stage: "",
    contract_value: 0,
    cash_collected: 0,
    start_window: "",
    end_window: "",
    notes: "",
    is_vexos: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [programsRes, clientsRes, typesRes, prefsRes] = await Promise.all([
        apiClient.get("/programs"),
        apiClient.get("/clients"),
        apiClient.get("/program-types"),
        // Preferences are non-critical — never block the page on them
        apiClient.get("/users/me/preferences").catch(() => ({ data: {} }))
      ]);
      setPrograms(programsRes.data);
      setClients(clientsRes.data);
      setProgramTypes(typesRes.data);

      // Column layout: account preference wins; fall back to this browser's
      // localStorage (and adopt it into the account so other devices match),
      // then to the defaults. Only seed on first load — later fetchData calls
      // (e.g. after a PDF import) must not clobber in-session changes.
      setListColumns(prev => {
        if (prev) return prev;
        const serverCols = sanitizeColumns(prefsRes.data?.programs_list_columns);
        if (serverCols) return serverCols;
        let localCols = null;
        try {
          localCols = sanitizeColumns(JSON.parse(localStorage.getItem(LIST_COLUMNS_STORAGE_KEY)));
        } catch { /* ignore corrupt localStorage */ }
        if (localCols) {
          apiClient.patch("/users/me/preferences", { programs_list_columns: localCols }).catch(() => {});
          return localCols;
        }
        return DEFAULT_LIST_COLUMNS;
      });
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Persist column layout changes to the user's account, with localStorage as
  // an offline fallback.
  const handleListColumnsChange = (next) => {
    setListColumns(next);
    try {
      localStorage.setItem(LIST_COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch { /* storage full/blocked — account save still applies */ }
    apiClient.patch("/users/me/preferences", { programs_list_columns: next }).catch(() => {
      toast.error("Couldn't save column layout to your account");
    });
  };

  // Open edit dialog
  const handleEditProgram = (program) => {
    setEditFormData({
      program_name: program.program_name || "",
      stage: program.stage || "Intake",
      contract_value: program.contract_value || 0,
      cash_collected: program.cash_collected || 0,
      start_window: program.start_window ? program.start_window.split("T")[0] : "",
      end_window: program.end_window ? program.end_window.split("T")[0] : "",
      notes: program.notes || "",
      is_vexos: program.has_vexos ?? false
    });
    setEditingProgram(program);
  };

  // Save edited program
  const handleSaveProgram = async () => {
    if (!editingProgram) return;
    try {
      const payload = { ...editFormData };
      // Only send the manual VEXOS tag when the user actually changed it, so
      // untouched programs keep inferring VEXOS from their scope/deliverables.
      if (payload.is_vexos === (editingProgram.has_vexos ?? false)) {
        delete payload.is_vexos;
      }
      const response = await apiClient.patch(`/programs/${editingProgram.id}`, payload);
      setPrograms(programs.map(p => p.id === editingProgram.id ? { ...p, ...response.data } : p));
      setEditingProgram(null);
      toast.success("Program updated");
    } catch (error) {
      console.error("Program update error:", error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message).join(', '));
      } else {
        toast.error("Failed to update program");
      }
    }
  };

  // Quick VEXOS tag toggle from the list view. Optimistic update; the backend
  // stores the manual tag and writes the change to the activity log.
  const handleToggleVexos = async (program) => {
    const next = !program.has_vexos;
    setPrograms(prev => prev.map(p => p.id === program.id ? { ...p, has_vexos: next, is_vexos: next } : p));
    try {
      await apiClient.patch(`/programs/${program.id}`, { is_vexos: next });
      toast.success(next ? `${program.program_name} tagged as VEXOS` : `VEXOS tag removed from ${program.program_name}`);
    } catch (error) {
      setPrograms(prev => prev.map(p => p.id === program.id ? { ...p, has_vexos: !next, is_vexos: program.is_vexos } : p));
      toast.error(error.response?.data?.detail || "Failed to update VEXOS tag");
    }
  };

  // Update program timeline (for Gantt drag)
  const handleUpdateTimeline = async (programId, startWindow, endWindow) => {
    try {
      const response = await apiClient.patch(`/programs/${programId}`, {
        start_window: startWindow,
        end_window: endWindow
      });
      setPrograms(programs.map(p => p.id === programId ? { ...p, ...response.data } : p));
      toast.success("Timeline updated");
    } catch (error) {
      toast.error("Failed to update timeline");
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    try {
      const response = await apiClient.post("/clients", newClient);
      setClients([...clients, response.data]);
      setNewClient({ name: "", primary_contact: "" });
      setShowNewClient(false);
      toast.success("Client created");
    } catch (error) {
      toast.error("Failed to create client");
    }
  };

  const handleCreateProgramType = async () => {
    if (!newProgramType.name.trim()) {
      toast.error("Program type name is required");
      return;
    }
    try {
      const response = await apiClient.post("/program-types", newProgramType);
      setProgramTypes([...programTypes, response.data]);
      setNewProgramType({ name: "", default_timeline_days: 30, revision_policy: "2 rounds included" });
      setShowNewProgramType(false);
      toast.success("Program type created");
    } catch (error) {
      toast.error("Failed to create program type");
    }
  };

  const handleCreateProgram = async () => {
    if (!newProgram.program_name.trim() || !newProgram.client_id || !newProgram.program_type_id) {
      toast.error("Please fill all required fields");
      return;
    }
    try {
      const response = await apiClient.post("/programs", newProgram);
      setPrograms([...programs, response.data]);
      setNewProgram({
        program_name: "",
        program_type_id: "",
        client_id: "",
        contract_value: 0,
        start_window: "",
        end_window: ""
      });
      setShowNewProgram(false);
      toast.success("Program created");
    } catch (error) {
      toast.error("Failed to create program");
    }
  };

  const handlePdfUpload = async () => {
    if (!pdfFile) {
      toast.error("Please select a PDF file");
      return;
    }
    
    setParsing(true);
    const formData = new FormData();
    formData.append("file", pdfFile);
    
    try {
      const response = await apiClient.post("/parse-agreement", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setParsedData(response.data);
      toast.success("Agreement parsed successfully");
    } catch (error) {
      toast.error("Failed to parse agreement");
    } finally {
      setParsing(false);
    }
  };

  // Full import - creates client, program, deliverables, and milestones automatically
  const importFromPdf = async () => {
    if (!parsedData || !pdfFile) return;
    
    setImporting(true);
    try {
      // Step 1: Create or find client
      let clientId = "";
      if (parsedData.client_name) {
        const existingClient = clients.find(c => 
          c.name.toLowerCase() === parsedData.client_name.toLowerCase()
        );
        if (existingClient) {
          clientId = existingClient.id;
        } else {
          // Create new client
          const clientRes = await apiClient.post("/clients", {
            name: parsedData.client_name,
            primary_contact: ""
          });
          clientId = clientRes.data.id;
          setClients([...clients, clientRes.data]);
          toast.success("Client '" + parsedData.client_name + "' created");
        }
      }

      if (!clientId) {
        toast.error("Could not determine client from PDF");
        setImporting(false);
        return;
      }

      // Step 2: Ensure we have a program type
      let programTypeId = programTypes[0]?.id;
      if (!programTypeId) {
        // Create a default program type
        const typeRes = await apiClient.post("/program-types", {
          name: "Standard Project",
          default_timeline_days: 30,
          revision_policy: "2 rounds included"
        });
        programTypeId = typeRes.data.id;
        setProgramTypes([...programTypes, typeRes.data]);
      }

      // Step 3: Create the program
      const programRes = await apiClient.post("/programs", {
        program_name: parsedData.program_name || "Imported Program",
        program_type_id: programTypeId,
        client_id: clientId,
        contract_value: parsedData.contract_value || 0,
        start_window: parsedData.start_date || "",
        end_window: parsedData.end_date || "",
        timeline_weeks_min: parsedData.timeline_weeks_min || null,
        timeline_weeks_max: parsedData.timeline_weeks_max || null
      });
      const programId = programRes.data.id;
      toast.success("Program '" + programRes.data.program_name + "' created");

      // Step 4: Upload the PDF agreement to the program
      const formData = new FormData();
      formData.append("file", pdfFile);
      await apiClient.post("/programs/" + programId + "/agreement", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Agreement PDF attached to program");

      // Step 5: Create deliverables from PDF
      const deliverables = parsedData.deliverables || [];
      for (const delivName of deliverables) {
        if (delivName && delivName.trim()) {
          await apiClient.post("/deliverables", {
            program_id: programId,
            deliverable_type: delivName.trim(),
            quantity: 1,
            due_window: parsedData.end_date || ""
          });
        }
      }
      if (deliverables.length > 0) {
        toast.success(deliverables.length + " deliverables added");
      }

      // Step 6: Create milestones from PDF
      const milestones = parsedData.milestones || [];
      for (const milestone of milestones) {
        if (milestone && milestone.name) {
          await apiClient.post("/milestones", {
            program_id: programId,
            name: milestone.name,
            date_window: milestone.date || parsedData.end_date || "",
            approval_required: false
          });
        }
      }
      if (milestones.length > 0) {
        toast.success(milestones.length + " milestones added");
      }

      // Refresh programs list
      await fetchData();
      
      // Close modal and reset
      setShowPdfUpload(false);
      setParsedData(null);
      setPdfFile(null);
      
      toast.success("Import complete! All data from agreement has been added.");
      
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const applyParsedData = () => {
    if (!parsedData) return;
    
    // Find or create client
    let clientId = "";
    if (parsedData.client_name) {
      const existingClient = clients.find(c => 
        c.name.toLowerCase() === parsedData.client_name.toLowerCase()
      );
      if (existingClient) {
        clientId = existingClient.id;
      }
    }
    
    // Store deliverables for later
    setPendingDeliverables(parsedData.deliverables || []);
    
    setNewProgram({
      program_name: parsedData.program_name || "",
      program_type_id: programTypes[0]?.id || "",
      client_id: clientId,
      contract_value: parsedData.contract_value || 0,
      start_window: parsedData.start_date || "",
      end_window: parsedData.end_date || ""
    });
    
    setShowPdfUpload(false);
    setShowNewProgram(true);
    setParsedData(null);
    setPdfFile(null);
  };

  // Handle drag end for program cards (moving between stage columns)
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;

    // Dropped outside any droppable
    if (!destination) return;

    // Dropped in same position
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    // Handle column reordering
    if (type === "COLUMN") {
      const newOrder = Array.from(columnOrder);
      const [removed] = newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, removed);
      setColumnOrder(newOrder);
      localStorage.setItem("programs-column-order", JSON.stringify(newOrder));
      toast.success("Column order saved");
      return;
    }

    // Handle program card movement between columns
    const newStage = destination.droppableId;
    const programId = draggableId;

    // Optimistically update UI
    setPrograms(prev => prev.map(p => 
      p.id === programId ? { ...p, stage: newStage } : p
    ));

    try {
      await apiClient.patch(`/programs/${programId}`, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch (error) {
      // Revert on failure
      fetchData();
      toast.error("Failed to update stage");
    }
  };

  // Reset column order to default
  const resetColumnOrder = () => {
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    localStorage.removeItem("programs-column-order");
    toast.success("Column order reset to default");
  };


  // Filter and sort programs
  const filteredPrograms = programs
    .filter(p => {
      // PM Test Mode filter - only show this PM's programs
      if (pmTestMode.isActive && pmTestMode.pmId) {
        if (!isProgramVisibleToPm(p, pmTestMode.pmId, pmTestMode.pmName)) {
          return false;
        }
      }
      
      // Filter by tab: in flight / on the horizon / on hold / delivered & closed
      if (tabForProgram(p) !== activeTab) return false;

      const matchesSearch = p.program_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           p.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesVexos = !vexosFilter || p.has_vexos;
      return matchesSearch && matchesVexos;
    })
    // Newest first. /programs comes back in insertion order (oldest first), so
    // reversing it first breaks created_at ties — and orders any old record
    // with no created_at — newest-first too. The sort itself is stable.
    .reverse()
    .sort((a, b) => {
      const ca = String(a.created_at || "");
      const cb = String(b.created_at || "");
      return ca < cb ? 1 : ca > cb ? -1 : 0;
    });

  // VEXOS program count within the current tab (for the filter chip)
  const vexosCount = programs.filter(
    p => tabForProgram(p) === activeTab && p.has_vexos
  ).length;

  // Per-tab totals for the badges on the tab row itself.
  const tabCounts = programs.reduce(
    (acc, p) => { acc[tabForProgram(p)] += 1; return acc; },
    { active: 0, horizon: 0, hold: 0, archived: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="programs-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="heading-1 text-foreground">{t("nav.programs")}</h1>
          <p className="body-text mt-1">
            {filteredPrograms.length} {pmTestMode.isActive ? 'assigned' : 'total'} {t("nav.programs").toLowerCase()}
            {pmTestMode.isActive && <span className="text-purple-600 ml-1">(PM View)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => setShowPdfUpload(true)}
            className="rounded-full flex-1 sm:flex-none"
            data-testid="upload-pdf-btn"
          >
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Upload Agreement</span>
          </Button>
          <Button
            onClick={() => setShowNewProgram(true)}
            className="rounded-full flex-1 sm:flex-none"
            data-testid="new-program-btn"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Program</span>
          </Button>
        </div>
      </div>

      {/* Financial Summary Strip - Hidden for PMs and PM Test Mode */}
      {canViewFinancials && !pmTestMode.isActive && (
        <FinancialSummaryStrip filterStatus="all" />
      )}

      {/* Active / On the Horizon / On Hold / Archived Tabs */}
      <div className="border-b border-border mb-4">
        <div className="flex gap-1">
          {[
            ["active", "Active"],
            ["horizon", "On the Horizon"],
            ["hold", "On Hold"],
            ["archived", "Delivered / Closed"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
              }`}
              data-testid={key === "archived" ? "tab-archived" : `tab-${key}`}
            >
              {label}
              <span className="ml-2 text-xs bg-secondary px-2 py-0.5 rounded-full">
                {tabCounts[key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Filters and View Toggle */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search programs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 rounded-lg"
              data-testid="search-input"
            />
          </div>
          
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
            <button
              onClick={() => setVexosFilter(!vexosFilter)}
              className={`h-10 px-4 rounded-lg text-sm font-semibold flex-shrink-0 border transition-all flex items-center gap-1.5 ${
                vexosFilter
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                  : "bg-background text-muted-foreground border-input hover:bg-accent hover:text-foreground"
              }`}
              data-testid="filter-vexos"
            >
              <Building2 className="w-4 h-4" />
              VEXOS
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                vexosFilter ? "bg-white/20" : "bg-secondary"
              }`}>
                {vexosCount}
              </span>
            </button>
          </div>
        </div>

        {/* View Toggle - Apple Style Segmented Control */}
        <div className="flex items-center p-1 bg-secondary/80 rounded-lg self-start lg:self-auto" data-testid="view-toggle">
          <button
            onClick={() => setViewMode("grid")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === "grid" 
                ? "bg-white text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="view-grid-btn"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Grid</span>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === "list" 
                ? "bg-white text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="view-list-btn"
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">List</span>
          </button>
          <button
            onClick={() => setViewMode("kanban")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === "kanban" 
                ? "bg-white text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="view-kanban-btn"
          >
            <Columns className="w-4 h-4" />
            <span className="hidden sm:inline">Board</span>
          </button>
          <button
            onClick={() => setViewMode("gantt")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
              viewMode === "gantt" 
                ? "bg-white text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="view-gantt-btn"
          >
            <GanttChart className="w-4 h-4" />
            <span className="hidden sm:inline">Gantt</span>
          </button>
        </div>
      </div>

      {/* Empty State */}
      {filteredPrograms.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <p className="text-muted-foreground mb-4">No programs found</p>
          <Button onClick={() => setShowNewProgram(true)} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Create your first program
          </Button>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrograms.map((program) => (
                <div
                  key={program.id}
                  className="block"
                  data-testid={`program-card-${program.id}`}
                >
                  <Card className="border border-border card-hover h-full group overflow-hidden">
                    {/* Cover Image Section - Always shown */}
                    <div className="relative aspect-[16/9] bg-zinc-900 overflow-hidden">
                      {program.cover_image_id && (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/programs/${program.id}/cover-image`}
                          alt={program.program_name}
                          className="absolute top-0 left-0 w-full h-full object-cover"
                          style={{ zIndex: 1 }}
                        />
                      )}
                      
                      {!program.cover_image_id && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ zIndex: 15 }}>
                          {/* Show project name prominently when no cover image */}
                          <h3 className="font-semibold text-lg text-white mb-4 text-center px-4" style={{ fontFamily: 'var(--font-sans)' }}>
                            {program.program_name}
                          </h3>
                          <label 
                            className="cursor-pointer flex flex-col items-center gap-2 text-zinc-400 hover:text-zinc-200 transition-colors p-4 rounded-lg hover:bg-white/10 border border-dashed border-zinc-600 hover:border-zinc-400"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onClick={(e) => e.stopPropagation()}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const file = e.target.files?.[0];
                                if (file) {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    await apiClient.post(`/programs/${program.id}/cover-image`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    });
                                    toast.success('Cover image uploaded');
                                    fetchData();
                                  } catch (err) {
                                    toast.error('Failed to upload image');
                                  }
                                }
                              }}
                            />
                            <Upload className="w-6 h-6" />
                            <span className="text-xs font-medium">Add Cover</span>
                          </label>
                        </div>
                      )}
                      
                      {/* Title overlay - only show when there is an image */}
                      {program.cover_image_id && (
                        <>
                          <Link to={`/programs/${program.id}`} className="absolute inset-0 bg-transparent" style={{ zIndex: 10 }}>
                            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-4">
                              <h3 className="font-semibold text-lg text-white truncate" style={{ fontFamily: 'var(--font-sans)' }}>
                                {program.program_name}
                              </h3>
                              <p className="text-sm text-white/70 truncate">
                                {program.client_name}
                              </p>
                            </div>
                          </Link>
                          {/* Edit cover image buttons - appear on hover */}
                          <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1" style={{ zIndex: 25 }}>
                            <label 
                              className="cursor-pointer bg-black/60 hover:bg-black/80 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onClick={(e) => e.stopPropagation()}
                                onChange={async (e) => {
                                  e.stopPropagation();
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      await apiClient.post(`/programs/${program.id}/cover-image`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                      });
                                      toast.success('Cover image updated');
                                      fetchData();
                                    } catch (err) {
                                      toast.error('Failed to upload image');
                                    }
                                  }
                                }}
                              />
                              <Upload className="w-3 h-3" />
                              Replace
                            </label>
                            <button
                              className="bg-black/60 hover:bg-red-600/80 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                try {
                                  await apiClient.delete(`/programs/${program.id}/cover-image`);
                                  toast.success('Cover image removed');
                                  fetchData();
                                } catch (err) {
                                  toast.error('Failed to remove image');
                                }
                              }}
                            >
                              <X className="w-3 h-3" />
                              Remove
                            </button>
                          </div>
                        </>
                      )}
                      
                      {/* Clickable overlay when no image - positioned below upload area */}
                      {!program.cover_image_id && (
                        <Link to={`/programs/${program.id}`} className="absolute inset-0 bg-transparent" style={{ zIndex: 5 }}>
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-sm text-zinc-400 truncate text-center">
                              {program.client_name}
                            </p>
                          </div>
                        </Link>
                      )}
                      
                      {/* Stage badge - clean styling without artifacts */}
                      <div className="absolute top-2 right-2 flex items-center gap-1.5" style={{ zIndex: 20 }}>
                        {program.has_vexos && (
                          <span className="inline-flex items-center gap-1 bg-indigo-600 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
                            <Building2 className="w-3 h-3" />
                            VEXOS
                          </span>
                        )}
                        <span className={`${getStageBadgeClass(program.stage)} px-2.5 py-0.5 rounded-full text-xs font-medium border-0`}>
                          {program.stage || "Queued"}
                        </span>
                      </div>
                    </div>
                    <Link to={`/programs/${program.id}`}>
                      <CardContent className="p-4">
                        <div className="space-y-4">
                          {/* No deliverables count on the card. It answered a
                              question nobody asks from the grid, and on most
                              projects it read 0/N or "No milestones configured".
                              The List view still has a Deliverables column for
                              anyone who wants to sort by it. */}

                          {/* Financial Data - Hidden for PMs and PM Test Mode */}
                          {canViewFinancials && !pmTestMode.isActive && (
                            <>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Contract Value</span>
                                <span className="font-medium">{formatCurrency(program.contract_value)}</span>
                              </div>

                              <div>
                                <div className="flex items-center justify-between text-xs mb-1.5">
                                  <span className="text-muted-foreground">Collected</span>
                                  <span className="text-green-600 font-medium">
                                    {formatCurrency(program.cash_collected)} ({getProgress(program)}%)
                                  </span>
                                </div>
                                <Progress value={getProgress(program)} className="h-1.5" />
                              </div>
                            </>
                          )}

                          {program.end_window && (
                            <div className="flex items-center justify-end pt-2 border-t border-border">
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(program.end_window).toLocaleDateString()}
                              </div>
                            </div>
                          )}

                        {/* Assigned PM — primary_pm_* comes from program_assignments,
                            the same source the Management View lanes use. The legacy
                            program_manager_* fields are only a fallback; preferring
                            them showed the PM from before a reassignment. */}
                        {(program.primary_pm_name || program.program_manager_name) && (
                          <div
                            className="flex items-center gap-2 pt-2 border-t border-border cursor-pointer hover:text-primary transition-colors"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Link to the program manager's view
                              const pmId = program.primary_pm_id || program.program_manager_id;
                              if (pmId) {
                                window.location.href = `/studio/management?pm=${pmId}&highlight=${program.id}`;
                              }
                            }}
                            data-testid={`pm-link-${program.id}`}
                          >
                            <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-medium">
                              {(program.primary_pm_name || program.program_manager_name).charAt(0)}
                            </div>
                            <span className="text-xs font-medium truncate">
                              {program.primary_pm_name || program.program_manager_name}
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                    </Link>
                  </Card>
                </div>
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <ProgramsListTable
              programs={filteredPrograms}
              showMoney={canViewFinancials && !pmTestMode.isActive}
              onToggleVexos={handleToggleVexos}
              columns={listColumns || DEFAULT_LIST_COLUMNS}
              onColumnsChange={handleListColumnsChange}
            />
          )}

          {/* Kanban/Board View with Drag & Drop */}
          {viewMode === "kanban" && (
            <DragDropContext onDragEnd={handleDragEnd}>
              {/* Column Settings Button */}
              <div className="flex justify-end mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="column-settings-btn"
                >
                  <Settings2 className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{showColumnSettings ? "Done" : "Configure Columns"}</span>
                  <span className="sm:hidden">{showColumnSettings ? "Done" : "Config"}</span>
                </Button>
              </div>

              {/* Column Settings Panel */}
              {showColumnSettings && (
                <Card className="mb-4 p-3 md:p-4 border border-border bg-secondary/20">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs md:text-sm font-medium">Drag to reorder:</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetColumnOrder}
                      className="text-xs"
                    >
                      Reset
                    </Button>
                  </div>
                  <Droppable droppableId="column-order" direction="horizontal" type="COLUMN">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex gap-2 flex-wrap overflow-x-auto pb-2"
                      >
                        {columnOrder.map((status, index) => (
                          <Draggable key={status} draggableId={`col-${status}`} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg border touch-manipulation ${
                                  snapshot.isDragging 
                                    ? "bg-white shadow-lg border-primary" 
                                    : "bg-white border-border"
                                }`}
                              >
                                <GripVertical className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground" />
                                <Badge className={`${getStageBadgeClass(status)} rounded-full text-[10px] md:text-xs`}>
                                  {status}
                                </Badge>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </Card>
              )}

              {/* Mobile tip */}
              <p className="text-xs text-muted-foreground mb-3 md:hidden">
                ← Scroll horizontally to see all columns. Long-press and drag cards to move.
              </p>

              {/* Kanban Board */}
              <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 -mx-4 px-4 md:mx-0 md:px-0 snap-x snap-mandatory md:snap-none">
                {/* Filter columns based on active tab. On the Horizon is the one
                    tab that isn't a set of stages — an unsigned project still sits
                    in Queued or Production — so it borrows the in-flight columns
                    and lets the card filter do the narrowing. Without this the
                    board would come up empty, since no stage maps to "horizon". */}
                {columnOrder
                  .filter(stage => tabForStage(stage) === (activeTab === "horizon" ? "active" : activeTab))
                  .map((stage) => {
                  const stagePrograms = filteredPrograms.filter(p => p.stage === stage);
                  // Unsigned (on the horizon) work is listed in the column but not
                  // counted in its dollar figure — see backend/services/horizon.py.
                  const stageTotal = stagePrograms.reduce(
                    (sum, p) => sum + (p.on_horizon ? 0 : (p.contract_value || 0)), 0
                  );
                  
                  return (
                    <div 
                      key={stage} 
                      className="flex-shrink-0 w-72 md:w-80 snap-center md:snap-align-none"
                      data-testid={`kanban-column-${stage}`}
                    >
                      {/* Column Header */}
                      <div className="bg-secondary/30 rounded-t-xl px-3 md:px-4 py-2 md:py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 md:gap-2">
                            <Badge className={`${getStageBadgeClass(stage)} rounded-full text-[10px] md:text-xs`}>
                              {stage}
                            </Badge>
                            <span className="text-xs md:text-sm font-medium text-muted-foreground">
                              {stagePrograms.length}
                            </span>
                          </div>
                          {canViewFinancials && !pmTestMode.isActive && (
                            <span className="text-[10px] md:text-xs text-muted-foreground">{formatCurrency(stageTotal)}</span>
                          )}
                        </div>
                      </div>

                      {/* Droppable Column */}
                      <Droppable droppableId={stage} type="PROGRAM">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`bg-secondary/30 rounded-b-xl p-3 md:p-4 min-h-[150px] md:min-h-[200px] transition-colors ${
                              snapshot.isDraggingOver ? "bg-primary/10 ring-2 ring-primary/30" : ""
                            }`}
                          >
                            {stagePrograms.length === 0 ? (
                              <div className="py-8 text-center text-sm text-muted-foreground border-2 border-dashed border-border rounded-lg">
                                Drop programs here
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {stagePrograms.map((program, index) => (
                                  <Draggable 
                                    key={program.id} 
                                    draggableId={program.id} 
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={`${snapshot.isDragging ? "rotate-2 shadow-xl" : ""}`}
                                      >
                                        <Card className={`border bg-white transition-all cursor-grab active:cursor-grabbing ${
                                          snapshot.isDragging 
                                            ? "border-primary shadow-xl" 
                                            : "border-border hover:shadow-md"
                                        }`}>
                                          <CardContent className="p-4">
                                            <div className="flex items-start gap-2">
                                              {/* Drag Handle */}
                                              <div 
                                                {...provided.dragHandleProps}
                                                className="mt-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
                                              >
                                                <GripVertical className="w-4 h-4" />
                                              </div>
                                              
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2 mb-3">
                                                  <Link to={`/programs/${program.id}`} className="min-w-0 flex-1 hover:text-primary transition-colors">
                                                    <h4 className="font-medium text-sm truncate" style={{ fontFamily: 'var(--font-sans)' }}>
                                                      {program.program_name}
                                                    </h4>
                                                    <p className="text-xs text-muted-foreground truncate">{program.client_name}</p>
                                                  </Link>
                                                </div>
                                                
                                                <div className="flex items-center gap-4 text-xs">
                                                  {canViewFinancials && !pmTestMode.isActive && (
                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                      <DollarSign className="w-3 h-3" />
                                                      <span>{formatCurrency(program.contract_value)}</span>
                                                    </div>
                                                  )}
                                                  {program.end_window && (
                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                      <Clock className="w-3 h-3" />
                                                      <span>{new Date(program.end_window).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                                    </div>
                                                  )}
                                                </div>

                                                {canViewFinancials && !pmTestMode.isActive && (
                                                  <div className="mt-3">
                                                    <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                                                      <span>Collected</span>
                                                      <span className="text-green-600">{getProgress(program)}%</span>
                                                    </div>
                                                    <Progress value={getProgress(program)} className="h-1" />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </CardContent>
                                        </Card>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            </DragDropContext>
          )}

          {/* Gantt View */}
          {viewMode === "gantt" && (
            <GanttView 
              programs={filteredPrograms} 
              onEditProgram={handleEditProgram}
              onUpdateTimeline={handleUpdateTimeline}
              getStageBadgeClass={getStageBadgeClass}
              readOnly={true}
            />
          )}
        </>
      )}

      {/* New Program Dialog */}
      <Dialog open={showNewProgram} onOpenChange={setShowNewProgram}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>New Program</DialogTitle>
            <DialogDescription>Create a new program with client and type selection.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Program Name *</Label>
              <Input
                value={newProgram.program_name}
                onChange={(e) => setNewProgram({ ...newProgram, program_name: e.target.value })}
                placeholder="Q1 Brand Campaign"
                data-testid="input-program-name"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Client *</Label>
                <button
                  type="button"
                  onClick={() => setShowNewClient(true)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  + New Client
                </button>
              </div>
              <Select
                value={newProgram.client_id}
                onValueChange={(value) => setNewProgram({ ...newProgram, client_id: value })}
              >
                <SelectTrigger data-testid="select-client">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Program Type *</Label>
                <button
                  type="button"
                  onClick={() => setShowNewProgramType(true)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  + New Type
                </button>
              </div>
              <Select
                value={newProgram.program_type_id}
                onValueChange={(value) => setNewProgram({ ...newProgram, program_type_id: value })}
              >
                <SelectTrigger data-testid="select-program-type">
                  <SelectValue placeholder="Select program type" />
                </SelectTrigger>
                <SelectContent>
                  {programTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Contract Value</Label>
              <CurrencyInput
                value={newProgram.contract_value}
                onChange={(value) => setNewProgram({ ...newProgram, contract_value: value })}
                placeholder="50,000"
                data-testid="input-contract-value"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newProgram.start_window}
                  onChange={(e) => setNewProgram({ ...newProgram, start_window: e.target.value })}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newProgram.end_window}
                  onChange={(e) => setNewProgram({ ...newProgram, end_window: e.target.value })}
                  data-testid="input-end-date"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProgram(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleCreateProgram} className="rounded-full" data-testid="create-program-btn">
              Create Program
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Client Dialog */}
      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>New Client</DialogTitle>
            <DialogDescription>Add a new client to the system.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="Acme Corp"
                data-testid="input-client-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Primary Contact</Label>
              <Input
                value={newClient.primary_contact}
                onChange={(e) => setNewClient({ ...newClient, primary_contact: e.target.value })}
                placeholder="john@acme.com"
                data-testid="input-client-contact"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewClient(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleCreateClient} className="rounded-full" data-testid="create-client-btn">
              Create Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Program Type Dialog */}
      <Dialog open={showNewProgramType} onOpenChange={setShowNewProgramType}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>New Program Type</DialogTitle>
            <DialogDescription>Define a new program type template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type Name *</Label>
              <Input
                value={newProgramType.name}
                onChange={(e) => setNewProgramType({ ...newProgramType, name: e.target.value })}
                placeholder="Brand Campaign"
                data-testid="input-type-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Timeline (days)</Label>
              <Input
                type="number"
                value={newProgramType.default_timeline_days}
                onChange={(e) => setNewProgramType({ ...newProgramType, default_timeline_days: parseInt(e.target.value) || 30 })}
                data-testid="input-type-timeline"
              />
            </div>
            <div className="space-y-2">
              <Label>Revision Policy</Label>
              <Input
                value={newProgramType.revision_policy}
                onChange={(e) => setNewProgramType({ ...newProgramType, revision_policy: e.target.value })}
                placeholder="2 rounds included"
                data-testid="input-type-revision"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProgramType(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleCreateProgramType} className="rounded-full" data-testid="create-type-btn">
              Create Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Upload Dialog */}
      <Dialog open={showPdfUpload} onOpenChange={setShowPdfUpload}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Upload Agreement</DialogTitle>
            <DialogDescription>Upload a PDF and auto-extract contract data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Upload a PDF agreement and we&apos;ll automatically extract client info, contract value, deliverables, and timeline.
            </p>
            
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              {pdfFile ? (
                <div className="flex items-center justify-center gap-3">
                  <span className="text-sm font-medium">{pdfFile.name}</span>
                  <button
                    onClick={() => setPdfFile(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Click to upload PDF</p>
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => setPdfFile(e.target.files[0])}
                    data-testid="pdf-input"
                  />
                </label>
              )}
            </div>

            {parsedData && (
              <div className="bg-secondary rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium mb-2">Extracted Data:</p>
                {parsedData.client_name && (
                  <p className="text-sm"><span className="text-muted-foreground">Client:</span> {parsedData.client_name}</p>
                )}
                {parsedData.program_name && (
                  <p className="text-sm"><span className="text-muted-foreground">Program:</span> {parsedData.program_name}</p>
                )}
                {parsedData.contract_value && (
                  <p className="text-sm"><span className="text-muted-foreground">Value:</span> {formatCurrency(parsedData.contract_value)}</p>
                )}
                {parsedData.start_date && (
                  <p className="text-sm"><span className="text-muted-foreground">Start:</span> {parsedData.start_date}</p>
                )}
                {parsedData.end_date && (
                  <p className="text-sm"><span className="text-muted-foreground">End:</span> {parsedData.end_date}</p>
                )}
                {parsedData.deliverables && parsedData.deliverables.length > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Deliverables ({parsedData.deliverables.length}):</span>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {parsedData.deliverables.slice(0, 5).map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                      {parsedData.deliverables.length > 5 && (
                        <li className="text-muted-foreground">+{parsedData.deliverables.length - 5} more...</li>
                      )}
                    </ul>
                  </div>
                )}
                {parsedData.milestones && parsedData.milestones.length > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Milestones ({parsedData.milestones.length}):</span>
                    <ul className="list-disc list-inside ml-2 mt-1">
                      {parsedData.milestones.slice(0, 3).map((m, i) => (
                        <li key={i}>{m.name || m}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowPdfUpload(false); setParsedData(null); setPdfFile(null); }} className="rounded-full">
              Cancel
            </Button>
            {parsedData ? (
              <>
                <Button variant="outline" onClick={applyParsedData} className="rounded-full" data-testid="apply-parsed-btn">
                  Edit First
                </Button>
                <Button 
                  onClick={importFromPdf} 
                  disabled={importing}
                  className="rounded-full bg-green-600 hover:bg-green-700" 
                  data-testid="import-all-btn"
                >
                  {importing ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                  ) : null}
                  Import All Data
                </Button>
              </>
            ) : (
              <Button 
                onClick={handlePdfUpload} 
                disabled={!pdfFile || parsing} 
                className="rounded-full"
                data-testid="parse-pdf-btn"
              >
                {parsing ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Parse Agreement"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Program Dialog */}
      <Dialog open={!!editingProgram} onOpenChange={(open) => !open && setEditingProgram(null)}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Edit Program</DialogTitle>
            <DialogDescription>Update program details and timeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Program Name</Label>
              <Input
                value={editFormData.program_name}
                onChange={(e) => setEditFormData({ ...editFormData, program_name: e.target.value })}
                data-testid="edit-program-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stage</Label>
                <Select
                  value={editFormData.stage}
                  onValueChange={(value) => setEditFormData({ ...editFormData, stage: value })}
                >
                  <SelectTrigger data-testid="edit-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAM_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={editFormData.start_window}
                  onChange={(e) => setEditFormData({ ...editFormData, start_window: e.target.value })}
                  data-testid="edit-start-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={editFormData.end_window}
                  onChange={(e) => setEditFormData({ ...editFormData, end_window: e.target.value })}
                  data-testid="edit-end-date"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contract Value</Label>
                <CurrencyInput
                  value={editFormData.contract_value}
                  onChange={(value) => setEditFormData({ ...editFormData, contract_value: value || 0 })}
                  data-testid="edit-contract-value"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Cash Collected</Label>
                <CurrencyInput
                  value={editFormData.cash_collected}
                  onChange={(value) => setEditFormData({ ...editFormData, cash_collected: value || 0 })}
                  data-testid="edit-cash-collected"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="edit-notes"
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Building2 className="w-4 h-4 text-indigo-500" />
                  VEXOS Program
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Tag this program as a VEXOS engagement
                </p>
              </div>
              <Switch
                checked={editFormData.is_vexos}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, is_vexos: checked })}
                data-testid="edit-is-vexos"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProgram(null)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleSaveProgram} className="rounded-full" data-testid="save-program-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramsView;
